import React, { useMemo, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import useRoutes, { getLinkComponentAttributes } from "../../hooks/useRoutes";
import { Trans, useTranslation } from "react-i18next";
import { useCheckout } from "../../providers/CheckoutProvider";

import { advertFormatCurrency } from "@/react/utils";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import Popup, {
  PopupActions,
  PopupButton,
  PopupContent,
  PopupHeadingContainer,
} from "../../../components/Common/Popup";
import PointBurst from "../PointBurst";
import Box from "@mui/material/Box";
import DigitalFileUpsaleIcon from "../../../components/Icons/DigitalFileUpsaleIcon";
import DigitalSetUpsaleIcon from "../../../components/Icons/DigitalSetUpsaleIcon";

const useStyles = makeStyles((theme) => ({
  header: {
    color: theme.palette.primary.dark,
    margin: 0,
    textAlign: "center",
  },
  popupHeaderContainer: {
    textAlign: "center",
    "& h2": {
      marginTop: 0,
      color: theme.palette.secondary.main,
      fontSize: "30px",
      fontWeight: 500,
      marginBottom: theme.spacing(1),
    },
    "& h3": {
      color: theme.palette.primary.dark,
      fontSize: "20px",
      fontWeight: 500,
      marginTop: 0,
      marginBottom: theme.spacing(1),
    },
  },
  singleFileRebatePrice: {
    fontSize: "20px",
  },
  singleFileRebateText: {
    fontSize: "12px",
    flex: 0,
  },
  singleFileRebateSaveText: {
    fontSize: "1rem",
  },
  productList: {
    display: "flex",
    justifyContent: "center",
    listStyle: "none",
    padding: 0,
    margin: 0,
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
    "& > li": {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        marginRight: theme.spacing(6),
        marginLeft: theme.spacing(6),
      },
    },
  },
  popupContainer: {
    [theme.breakpoints.up("sm")]: {
      minWidth: "500px",
    },
    marginBottom: 0,
  },
  iconContainer: {
    width: "100%",
    position: "relative",
  },
  pointBurstContainer: {
    position: "absolute",
    top: "0px",
    right: "100px",
  },
  saveText: {
    color: theme.palette.secondary.main,
    textAlign: "center",
    fontSize: "30px",
  },
  subjectText: {
    textAlign: "center",
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: "center",
    marginTop: theme.spacing(1),
  },
}));

const UpsaleDialogDigitalFiles = (props) => {
  return <DigitalFilesDialogue {...props} />;
};

export default UpsaleDialogDigitalFiles;

const DigitalFilesDialogue = ({
  handleNextStep,
  digitalProducts,
  subject,
  directAddToCart,
  fromPrice,
  maxSavingPercent,
}) => {
  const isMobile = useIsMobileDevice();
  const classes = useStyles();
  const { t } = useTranslation();
  const [error, setError] = useState(null);

  const product = useMemo(() => {
    for (const product of digitalProducts) {
      if (product.type === "set") {
        return product;
      }
    }
    return digitalProducts[0];
  }, [digitalProducts]);

  return (
    <Popup
      open={true}
      fullWidth={isMobile}
      maxWidth="lg"
      data-testid="special-offer-dialog"
    >
      <PopupHeadingContainer className={classes.popupHeaderContainer}>
        <h2>
          <Trans>Special offer</Trans>
        </h2>
        <h3>
          {/* The link-out arm covers whatever the digital portraits page
              turns out to offer, which is rarely just portrait sets. */}
          {directAddToCart
            ? t("Discounted digital portrait sets")
            : t("Discounted digital pictures")}
        </h3>
      </PopupHeadingContainer>
      <PopupContent className={classes.popupContainer}>
        <ul className={classes.productList}>
          <li>
            {directAddToCart ? (
              <UnitPricePointBurst product={product} />
            ) : (
              <FromPricePointBurst product={product} fromPrice={fromPrice} />
            )}
          </li>
        </ul>
        <div className={classes.saveText}>
          {directAddToCart
            ? t("Save %s!", {
                postProcess: "sprintf",
                sprintf: [
                  advertFormatCurrency(product.price - product.rebatedPrice),
                ],
              })
            : t("Save up to %s%%!", {
                postProcess: "sprintf",
                sprintf: [maxSavingPercent],
              })}
        </div>
        {directAddToCart && (
          <div className={classes.subjectText}>
            {t("All digital pictures of %s", {
              postProcess: "sprintf",
              sprintf: [subject.name],
            })}
          </div>
        )}
        {error && (
          <div
            className={classes.errorText}
            data-testid="digital-set-direct-add-error"
          >
            {error}
          </div>
        )}
      </PopupContent>
      <PopupActions>
        <PopupButton
          onClick={handleNextStep}
          secondary
          data-testid="decline-button"
        >
          <Trans>No thanks</Trans>
        </PopupButton>
        {directAddToCart ? (
          <DirectAddAction
            subject={subject}
            handleNextStep={handleNextStep}
            onError={setError}
          />
        ) : (
          <LinkOutAction />
        )}
      </PopupActions>
    </Popup>
  );
};

/** Today's offer: a plain link out to the digital portraits page. */
const LinkOutAction = () => {
  const routes = useRoutes();

  return (
    <PopupButton
      {...getLinkComponentAttributes("products.digitalportraits", routes)}
      data-testid="digital-set-offer-link-button"
    >
      <Trans>Check out offer</Trans>
    </PopupButton>
  );
};

/**
 * The treatment: adds the whole digital set for the child the session is
 * logged in with, without leaving checkout.
 */
const DirectAddAction = ({ subject, handleNextStep, onError }) => {
  const { buyDigitalSets } = useCheckout();
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  // `disabled` only takes effect on the next render, so it does not stop two
  // clicks landing in the same tick. This does.
  const inFlight = useRef(false);

  const handleAdd = () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setIsAdding(true);
    onError(null);

    buyDigitalSets([{ uuid: subject.uuid, name: subject.name }])
      .then(() => {
        // Deliberately leaves inFlight set: handleNextStep unmounts the
        // dialog, and clearing it would open a window for a second click.
        handleNextStep();
      })
      .catch(() => {
        inFlight.current = false;
        setIsAdding(false);
        onError(t("Could not add the digital pictures. Please try again."));
      });
  };

  return (
    <PopupButton
      onClick={handleAdd}
      disabled={isAdding}
      data-testid="digital-set-direct-add-button"
    >
      {isAdding ? (
        <Trans>Adding…</Trans>
      ) : (
        <Trans>Add all digital pictures</Trans>
      )}
    </PopupButton>
  );
};

/** The offer's product graphic with its price burst. */
const OfferIllustration = ({ product, children }) => {
  const classes = useStyles();

  return (
    <Box className={classes.iconContainer}>
      <div>
        {product.type === "set" ? (
          <DigitalSetUpsaleIcon />
        ) : (
          <DigitalFileUpsaleIcon />
        )}
        <div className={classes.pointBurstContainer}>
          <PointBurst variant="secondary" content={children} />
        </div>
      </div>
    </Box>
  );
};

/**
 * The link-out arm leads with the cheapest of everything the digital portraits
 * page will let this user pick, so the burst reads "from".
 */
const FromPricePointBurst = ({ product, fromPrice }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <OfferIllustration product={product}>
      <div>
        <div className={classes.singleFileRebateText}>
          {t("From {{lowest price}}", {
            skipInterpolation: true,
            postProcess: "ntlf-context",
          })}
        </div>
        <div className={classes.singleFileRebatePrice}>
          {advertFormatCurrency(fromPrice)}
        </div>
      </div>
    </OfferIllustration>
  );
};

/** The one-click arm adds one set at one price, so its burst quotes it. */
const UnitPricePointBurst = ({ product }) => {
  const classes = useStyles();

  return (
    <OfferIllustration product={product}>
      <div>
        <div className={classes.singleFileRebatePrice}>
          {advertFormatCurrency(product.rebatedPrice)}
        </div>
        <div className={classes.singleFileRebateText}>
          {product.type === "set" ? (
            <Trans>per set</Trans>
          ) : (
            <Trans>per file</Trans>
          )}
        </div>
      </div>
    </OfferIllustration>
  );
};
